import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Confetti from "react-confetti";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  AutoFixHigh,
  CheckCircle,
  Edit,
  ExpandMore as ExpandMoreIcon,
  PlayCircleOutline,
} from "@mui/icons-material";
import { send_request } from "../../../../utils/Request";
import { format } from "../../../../utils/Date";
import { CustomDialog } from "../../../Component/Dialog";
import { NodeReadOnlyField, NodeSection } from "../../../NodeUI";
import useProjectStore from "../../../ProjectCanvas/useProjectStore";
import TriggerNotInSyncWarning from "../ForceStart/TriggerComponentUtil/TriggerNotInSyncWarning";
import WorkflowPublishComment from "../WorkflowPublishComment";
import "./WorkflowPublishConfirmDialog.css";

const DESCRIPTION_OPTIONS = {
  MANUAL: "manual",
  AI: "ai",
};

const PUBLISH_STEPS = {
  CONFIRM: "confirm",
  PUBLISHING: "publishing",
  SUCCESS: "success",
};

function VersionChip({ variant, children }) {
  return (
    <span
      className={`workflow-publish-version-chip workflow-publish-version-chip--${variant}`}
    >
      {children}
    </span>
  );
}

function PublishVersionSummary({
  workflow,
  draftVersion,
  publishedVersion,
  lastModified,
  lastPublished,
  step,
}) {
  const timezone = useProjectStore.getState().timezone;
  const lastUpdatedText = lastModified
    ? format(lastModified, timezone, "relative")
    : "not saved yet";
  const publishedAtText = lastPublished ? format(lastPublished, timezone) : "";
  const productionText =
    step === PUBLISH_STEPS.SUCCESS
      ? `Production v${publishedVersion}`
      : `Current production v${publishedVersion}`;

  return (
    <div className="workflow-publish-summary">
      <p className="workflow-publish-summary__name">
        {workflow?.name || "Untitled workflow"}
      </p>
      <div className="workflow-publish-summary__meta">
        <VersionChip
          variant={step === PUBLISH_STEPS.SUCCESS ? "production" : "draft"}
        >
          {step === PUBLISH_STEPS.SUCCESS
            ? `Production v${publishedVersion}`
            : `Draft v${draftVersion}`}
        </VersionChip>
        {step !== PUBLISH_STEPS.SUCCESS && (
          <p className="workflow-publish-summary__timestamp">
            Last updated {lastUpdatedText}
          </p>
        )}
        {step === PUBLISH_STEPS.SUCCESS && publishedAtText && (
          <p className="workflow-publish-summary__timestamp">
            Published {publishedAtText}
          </p>
        )}
      </div>
      {step === PUBLISH_STEPS.SUCCESS ? (
        <p className="workflow-publish-summary__note">
          New canvas edits will save as
          <VersionChip variant="draft">Draft v{draftVersion}</VersionChip>
        </p>
      ) : (
        <p className="workflow-publish-summary__note">
          Publishing will replace{" "}
          <VersionChip variant="production">{productionText}</VersionChip>
        </p>
      )}
    </div>
  );
}

export const WorkflowPublishConfirmDialog = (props) => {
  const step =
    props.step ||
    (props.isPublishing ? PUBLISH_STEPS.PUBLISHING : PUBLISH_STEPS.CONFIRM);
  const [descriptionOption, setDescriptionOption] = useState(
    DESCRIPTION_OPTIONS.MANUAL,
  );
  const [manualDescription, setManualDescription] = useState("");
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState("");
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiGenerationError, setAiGenerationError] = useState("");
  const [syncStatus, setSyncStatus] = useState([]);

  const notInSync = useMemo(
    () => syncStatus.filter((s) => s.status === "NOT_IN_SYNC"),
    [syncStatus],
  );

  const getRequestErrorMessage = (response) => {
    const status = response?.response?.status;
    const data = response?.response?.data;
    const message =
      data?.message ||
      data?.error ||
      (typeof data === "string" ? data : null) ||
      response?.message;

    if (status === 402) {
      return (
        message ||
        "Your organization has no remaining AI credits for this action."
      );
    }
    if (status === 404) {
      return "Workflow summary generation is unavailable right now. Try again or edit manually.";
    }
    return (
      message ||
      "Failed to generate AI description. Try again or edit manually."
    );
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (props.open && step === PUBLISH_STEPS.CONFIRM) {
      setDescriptionOption(DESCRIPTION_OPTIONS.MANUAL);
      setManualDescription(props.workflow?.description || "");
      setAiGeneratedDescription("");
      setIsGeneratingAi(false);
      setAiGenerationError("");
    }
  }, [props.open, props.workflow?.description, step]);

  useEffect(() => {
    if (!props.open || step !== PUBLISH_STEPS.SUCCESS) {
      return;
    }

    const fetchPipedreamSyncStatus = async () => {
      try {
        const json = await send_request(
          `component-service/trigger_from_external_app/triggerSyncStatus/${props.projectId}`,
          null,
          null,
          "GET",
        );
        if (json && Array.isArray(json.data)) {
          setSyncStatus(json.data);
        }
      } catch (e) {
        setSyncStatus([]);
      }
    };
    fetchPipedreamSyncStatus();
  }, [props.open, props.projectId, step]);

  const handleGenerateAiDescription = async () => {
    setIsGeneratingAi(true);
    setAiGenerationError("");
    try {
      const response = await send_request(
        "ai-service/ai/project/generate-summary",
        null,
        { projectId: props.projectId },
        "post",
        undefined,
        undefined,
        null,
        90000,
      );

      if (response?.response) {
        setAiGenerationError(getRequestErrorMessage(response));
        return;
      }

      const generatedDescription = response?.data?.explanation;
      if (generatedDescription) {
        setAiGeneratedDescription(generatedDescription);
      } else {
        setAiGenerationError(
          "No description was returned. Try again or edit manually.",
        );
      }
    } catch (err) {
      console.error("Failed to generate AI description:", err);
      setAiGenerationError(
        "Failed to generate AI description. Try again or edit manually.",
      );
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const getDescriptionToPublish = () => {
    switch (descriptionOption) {
      case DESCRIPTION_OPTIONS.AI:
        return aiGeneratedDescription || props.workflow?.description;
      case DESCRIPTION_OPTIONS.MANUAL:
      default:
        return manualDescription;
    }
  };

  const handlePublishClick = () => {
    const description = getDescriptionToPublish();
    props.onClickPublishButton(description);
  };

  const handleFixTriggers = () => {
    const first = notInSync[0];
    if (!first) return;
    window.open(
      `/project/canvas/${props.projectId}?view=${first.componentId}`,
      "_blank",
    );
  };

  const renderDescriptionContent = () => {
    switch (descriptionOption) {
      case DESCRIPTION_OPTIONS.MANUAL:
        return (
          <textarea
            className="publish-description-textarea"
            value={manualDescription}
            onChange={(e) => setManualDescription(e.target.value)}
            placeholder="Enter a description for this workflow..."
            maxLength={1000}
          />
        );

      case DESCRIPTION_OPTIONS.AI:
        return (
          <div className="publish-description-ai-container">
            {isGeneratingAi ? (
              <div className="publish-description-skeleton">
                <div className="publish-description-skeleton-line" />
                <div className="publish-description-skeleton-line" />
                <div className="publish-description-skeleton-line" />
              </div>
            ) : aiGeneratedDescription ? (
              <div className="publish-description-ai-preview">
                {aiGeneratedDescription}
              </div>
            ) : (
              <div className="publish-description-ai-preview placeholder">
                Click generate to create an AI-powered description
              </div>
            )}
            <button
              className="publish-description-generate-btn"
              onClick={handleGenerateAiDescription}
              disabled={isGeneratingAi}
            >
              {isGeneratingAi ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutoFixHigh />
              )}
              {isGeneratingAi
                ? "Generating..."
                : aiGeneratedDescription
                ? "Regenerate"
                : "Generate with AI"}
            </button>
            {aiGenerationError && (
              <p className="publish-description-error">{aiGenerationError}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderConfirmContent = () => (
    <div className="workflow-publish-content">
      <PublishVersionSummary
        workflow={props.workflow}
        draftVersion={props.draftVersion}
        publishedVersion={props.publishedVersion}
        lastModified={props.workflow?.lastModified}
        lastPublished={props.lastPublished}
        step={PUBLISH_STEPS.CONFIRM}
      />

      <NodeSection title="Workflow description">
        <div className="publish-description-options">
          <button
            type="button"
            className={`publish-description-option ${
              descriptionOption === DESCRIPTION_OPTIONS.MANUAL ? "selected" : ""
            }`}
            onClick={() => setDescriptionOption(DESCRIPTION_OPTIONS.MANUAL)}
          >
            <Edit />
            Edit manually
          </button>
          <button
            type="button"
            className={`publish-description-option ${
              descriptionOption === DESCRIPTION_OPTIONS.AI ? "selected" : ""
            }`}
            onClick={() => setDescriptionOption(DESCRIPTION_OPTIONS.AI)}
          >
            <AutoFixHigh />
            Use AI
          </button>
        </div>
        <div className="publish-description-content">
          {renderDescriptionContent()}
        </div>
      </NodeSection>

      <WorkflowPublishComment setPublishComment={props.setPublishComment} />
    </div>
  );

  const renderPublishingContent = () => (
    <div className="workflow-publish-content">
      <PublishVersionSummary
        workflow={props.workflow}
        draftVersion={props.draftVersion}
        publishedVersion={props.publishedVersion}
        lastModified={props.workflow?.lastModified}
        lastPublished={props.lastPublished}
        step={PUBLISH_STEPS.PUBLISHING}
      />
      <NodeSection title="Publishing">
        <div className="workflow-publish-progress">
          <CircularProgress size={32} />
          <div>
            <p className="workflow-publish-progress__title">
              Publishing version {props.draftVersion}
            </p>
            <p className="workflow-publish-progress__desc">
              Saving the canvas, updating the production version, and refreshing
              workflow metadata.
            </p>
          </div>
        </div>
      </NodeSection>
    </div>
  );

  const renderSuccessContent = () => (
    <div className="workflow-publish-content">
      <PublishVersionSummary
        workflow={props.workflow}
        draftVersion={props.draftVersion}
        publishedVersion={props.publishedVersion}
        lastModified={props.workflow?.lastModified}
        lastPublished={props.lastPublished}
        step={PUBLISH_STEPS.SUCCESS}
      />

      <NodeSection title="Next steps">
        <div className="workflow-publish-next-steps">
          <div className="workflow-publish-next-step">
            <div className="workflow-publish-next-step__body">
              <p className="workflow-publish-next-step__title">
                Run in production
              </p>
              <p className="workflow-publish-next-step__desc">
                Start a live session to verify the published workflow end to
                end.
              </p>
            </div>
            <Button
              startIcon={<PlayCircleOutline />}
              variant="contained"
              onClick={props.onClickRunPublished}
              sx={{
                backgroundColor: "var(--color-success)",
                "&:hover": { backgroundColor: "#388e3c" },
              }}
            >
              Run
            </Button>
          </div>

          {notInSync.length > 0 && (
            <>
              <div className="workflow-publish-next-step workflow-publish-next-step--warn">
                <div className="workflow-publish-next-step__body">
                  <p className="workflow-publish-next-step__title">
                    Activate external triggers
                  </p>
                  <p className="workflow-publish-next-step__desc">
                    {notInSync.length === 1
                      ? "1 external trigger is not in sync with production. Activate it before relying on external events."
                      : `${notInSync.length} external triggers are not in sync with production. Activate them before relying on external events.`}
                  </p>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleFixTriggers}
                >
                  Fix triggers
                </Button>
              </div>
              <div className="workflow-publish-trigger-warning">
                <TriggerNotInSyncWarning
                  syncStatus={syncStatus}
                  projectId={props.projectId}
                />
              </div>
            </>
          )}
        </div>
      </NodeSection>

      <Accordion className="workflow-publish-details" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Version notes and undo
        </AccordionSummary>
        <AccordionDetails>
          <NodeReadOnlyField
            label="Publish notes"
            value={props.publishComment || "No notes or comments added"}
            showCopy={Boolean(props.publishComment)}
            monospace={false}
          />
          {props.workflow?.description && (
            <NodeReadOnlyField
              label="Workflow description"
              value={props.workflow.description}
              showCopy={false}
              monospace={false}
            />
          )}
          <div>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
              }}
            >
              Revert deployment
            </Typography>
            <p className="workflow-publish-revert-text">
              Roll back to a previous published version from
              <Link
                to={`/project/version_history/${props.projectId}`}
                className="link"
              >
                version history
              </Link>
              .
            </p>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );

  const renderContents = () => {
    switch (step) {
      case PUBLISH_STEPS.PUBLISHING:
        return renderPublishingContent();
      case PUBLISH_STEPS.SUCCESS:
        return renderSuccessContent();
      case PUBLISH_STEPS.CONFIRM:
      default:
        return renderConfirmContent();
    }
  };

  const renderTitle = () => {
    if (step === PUBLISH_STEPS.SUCCESS) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircle sx={{ color: "success.main", fontSize: 24 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Workflow successfully published
          </Typography>
        </Box>
      );
    }
    return step === PUBLISH_STEPS.PUBLISHING
      ? `Publishing version ${props.draftVersion}`
      : `Publish version ${props.draftVersion}`;
  };

  const renderButtons = () => {
    if (step === PUBLISH_STEPS.SUCCESS) {
      return (
        <Button
          onClick={props.onDonePublish}
          variant="outlined"
          color="primary"
        >
          Done
        </Button>
      );
    }

    return (
      <>
        <Button
          onClick={props.onClickCancelButton}
          disabled={step === PUBLISH_STEPS.PUBLISHING}
          variant={"outlined"}
          id="publish-dialog-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          onClick={handlePublishClick}
          variant={"contained"}
          color={"success"}
          disabled={
            step === PUBLISH_STEPS.PUBLISHING ||
            (descriptionOption === DESCRIPTION_OPTIONS.AI && isGeneratingAi)
          }
          id="publish-dialog-publish-btn"
        >
          {step === PUBLISH_STEPS.PUBLISHING ? "Publishing" : "Publish"}
          {step === PUBLISH_STEPS.PUBLISHING && (
            <CircularProgress size={24} className="buttonProgress" />
          )}
        </Button>
      </>
    );
  };

  return (
    <>
      {props.open && step === PUBLISH_STEPS.SUCCESS && (
        <Confetti
          gravity={0.2}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1301,
          }}
        />
      )}
      <CustomDialog
        isOpen={props.open}
        title={renderTitle()}
        zIndex={true}
        backdropStyle={"background"}
        publish={true}
        contents={renderContents()}
        buttons={renderButtons()}
      />
    </>
  );
};
